import React, { useState, useEffect } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons/faPencilAlt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppState } from '../../store';
import EditorButton from '../EditorButton';
import MyEcriAnnouncement from '../MyEcriAnnouncement';
import {
  SubscriptionState,
  SubscriptionLoadingStatus,
} from '../../store/subscriptions/types';
import { Component, ComponentPreference } from '@ecri/react-components';
import { updateSubscriptionPreferences } from '../../store/subscriptions/actions';
import { SubscriptionViewModel } from '../../model/subscriptions';
import { UserAuthProps } from '../../model/auth/types';
import { convertTimeStampToLastLoginDate } from '../../util/date';
import applicationIcon from '../../assets/myecri/app-blue.svg';
import publicationIcon from '../../assets/myecri/copy-blue.svg';
import resourcesIcon from '../../assets/myecri/button-diamond-top-blue.svg';
import servicesIcon from '../../assets/myecri/app-chat-blue.svg';
import styles from './mycontenteditor.module.scss';

interface EditableComponent extends Component {
  newDisplayState?: boolean;
}

type ComponentPreferenceLookup = {
  [k: string]: ComponentPreference;
};

interface ComponentAreaEditorProps {
  title: string;
  itemImage: string;
  items: EditableComponent[];
  onDisplayStateChange: (elt: ComponentPreference) => void;
  isEditing: boolean;
}

interface ComponentElementEditorProps {
  item: EditableComponent;
  itemImage: string;
  onDisplayStateChange: (elt: ComponentPreference) => void;
  isEditing: boolean;
}

const ComponentElementEditor: React.FC<ComponentElementEditorProps> = ({
  item,
  itemImage,
  isEditing,
  onDisplayStateChange,
}) => {
  const {
    title,
    newDisplayState,
    applicationUrl,
    url,
    showInMyEcri,
    uuid,
  } = item;
  const [isChecked, setChecked] = useState(newDisplayState);
  useEffect(() => {
    if (!isEditing) {
      setChecked(showInMyEcri);
    }
  }, [isEditing, showInMyEcri]);
  return (
    <div className={styles.componentEditor}>
      {isEditing ? (
        <>
          <input
            id={`cb-${uuid}`}
            name={`cb-${uuid}`}
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              const updatedCheck = !isChecked;
              setChecked(updatedCheck);
              onDisplayStateChange({
                uuid: item.uuid,
                showInMyEcri: updatedCheck,
              });
            }}
          />
          <label htmlFor={`cb-${uuid}`}>{title}</label>
        </>
      ) : (
        <>
          <img src={itemImage} alt={title} />
          <a
            href={
              applicationUrl && applicationUrl !== '' ? applicationUrl : url
            }
          >
            {title}
          </a>
        </>
      )}
    </div>
  );
};

const ComponentAreaEditor: React.FC<ComponentAreaEditorProps> = ({
  title,
  itemImage,
  items,
  isEditing,
  onDisplayStateChange,
}) => {
  if (!items || !items.length || items.length === 0) {
    return <div />;
  }
  const itemsToDisplay = items.filter((x) => x.showInMyEcri || isEditing);
  const halfway = Math.ceil(itemsToDisplay.length / 2);
  const leftSide = itemsToDisplay.slice(0, halfway);
  const rightSide = itemsToDisplay.slice(halfway, items.length);
  return (
    <>
      <h4>{title}</h4>
      <div className="row nomargin">
        {leftSide && leftSide.length > 0 ? (
          <div className="col-xs-12 col-sm-6">
            {leftSide.map((x) => (
              <ComponentElementEditor
                key={x.uuid}
                item={x}
                isEditing={isEditing}
                itemImage={itemImage}
                onDisplayStateChange={onDisplayStateChange}
              />
            ))}
          </div>
        ) : (
          <div>
            No items are currently visible. To make an item visible click the
            'Edit My Content' link above.
          </div>
        )}
        {rightSide && rightSide.length > 0 && (
          <div className="col-xs-12 col-sm-6">
            {rightSide.map((x) => (
              <ComponentElementEditor
                key={x.uuid}
                item={x}
                isEditing={isEditing}
                itemImage={itemImage}
                onDisplayStateChange={onDisplayStateChange}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

interface DispatchProps {
  updatePrefs: (token: string, updates: ComponentPreference[]) => void;
}

const MyContentEditor: React.FC<
  SubscriptionState & UserAuthProps & DispatchProps
> = ({
  loadState,
  userDataLoaded,
  allComponents,
  allComponentTypes,
  allMemberships,
  allMembershipCategories,
  user,
  updatePrefs,
}) => {
  const [vm, setViewModel] = useState<SubscriptionViewModel>(
    new SubscriptionViewModel({
      allComponents,
      allMemberships,
      allComponentTypes,
      allMembershipCategories,
    })
  );
  const [changedViewPrefs, setChangedViewPrefs] = useState<
    ComponentPreferenceLookup
  >({});

  const [showEditorToggle, setEditorToggleDisplay] = useState(true);

  useEffect(() => {
    setViewModel(
      new SubscriptionViewModel({
        allComponents,
        allMemberships,
        allComponentTypes,
        allMembershipCategories,
      })
    );

    setChangedViewPrefs({});
  }, [
    allComponentTypes,
    allComponents,
    allMembershipCategories,
    allMemberships,
  ]);

  useEffect(() => {
    if (loadState !== SubscriptionLoadingStatus.Error) {
      setChangedViewPrefs({});
      setEditorToggleDisplay(true);
    }
  }, [loadState]);

  if (
    loadState === SubscriptionLoadingStatus.Loading ||
    loadState === SubscriptionLoadingStatus.Initial ||
    !userDataLoaded
  ) {
    return <div />;
  }

  const { access_token = '' } = user || {};
  const { profile = null } = user || {};
  return (
    <div className={styles.myContentEditorContainer}>
      {profile?.auth_time && (
        <p>{convertTimeStampToLastLoginDate(profile.auth_time)}</p>
      )}
      <MyEcriAnnouncement />
      {showEditorToggle && (
        <p onClick={() => setEditorToggleDisplay(false)}>
          Customize the page to display only those items relevant to you.
          <span className={styles.openEditInput}>
            <FontAwesomeIcon icon={faPencilAlt} size="xs" />
            Edit My Content
          </span>
        </p>
      )}
      {loadState === SubscriptionLoadingStatus.Updating ? (
        <div>Saving your changes...</div>
      ) : (
        <div
          className={`${styles.componentEditorContainer} ${
            showEditorToggle ? '' : styles.editing
          }`}
        >
          {!showEditorToggle && (
            <div className={styles.updateButtons}>
              <p>
                Customize your shortcuts by deselecting items you wish to hide:
              </p>
              <EditorButton
                type="Cancel"
                onClick={() => {
                  setViewModel(
                    new SubscriptionViewModel({
                      allComponents,
                      allMemberships,
                      allComponentTypes,
                      allMembershipCategories,
                    })
                  );
                  setEditorToggleDisplay(true);
                }}
              >
                Cancel
              </EditorButton>
              <EditorButton
                type="Update"
                onClick={() => {
                  updatePrefs(
                    access_token,
                    ((Object.keys(
                      changedViewPrefs
                    ) as unknown) as (keyof ComponentPreferenceLookup)[]).map(
                      (k) => changedViewPrefs[k]
                    )
                  );
                }}
              >
                Save
              </EditorButton>
            </div>
          )}
          <ComponentAreaEditor
            title="My Applications"
            itemImage={applicationIcon}
            isEditing={!showEditorToggle}
            items={vm
              .getComponentsWithDescription('Applications')
              .filter((x) => x.userHasAccess)
              .map((x) => ({
                ...x,
                newDisplayState: x.showInMyEcri,
              }))
              .sort((a, b) => (a.title < b.title ? -1 : 1))}
            onDisplayStateChange={(elt: ComponentPreference) =>
              setChangedViewPrefs({
                ...changedViewPrefs,
                [elt.uuid]: elt,
              })
            }
          />
          <ComponentAreaEditor
            title="My Publications"
            isEditing={!showEditorToggle}
            itemImage={publicationIcon}
            items={vm
              .getComponentsWithDescription('Publications')
              .filter((x) => x.userHasAccess)
              .map((x) => ({
                ...x,
                newDisplayState: x.showInMyEcri,
              }))
              .sort((a, b) => (a.title < b.title ? -1 : 1))}
            onDisplayStateChange={(elt: ComponentPreference) =>
              setChangedViewPrefs({
                ...changedViewPrefs,
                [elt.uuid]: elt,
              })
            }
          />
          <ComponentAreaEditor
            title="My Resources"
            isEditing={!showEditorToggle}
            itemImage={resourcesIcon}
            items={vm
              .getComponentsWithDescription('Resources')
              .filter((x) => x.userHasAccess)
              .map((x) => ({
                ...x,
                newDisplayState: x.showInMyEcri,
              }))
              .sort((a, b) => (a.title < b.title ? -1 : 1))}
            onDisplayStateChange={(elt: ComponentPreference) =>
              setChangedViewPrefs({
                ...changedViewPrefs,
                [elt.uuid]: elt,
              })
            }
          />
          <ComponentAreaEditor
            title="My Services"
            isEditing={!showEditorToggle}
            itemImage={servicesIcon}
            items={vm
              .getComponentsWithDescription('Customized Service')
              .filter((x) => x.userHasAccess)
              .map((x) => ({
                ...x,
                newDisplayState: x.showInMyEcri,
              }))
              .sort((a, b) => (a.title < b.title ? -1 : 1))}
            onDisplayStateChange={(elt: ComponentPreference) =>
              setChangedViewPrefs({
                ...changedViewPrefs,
                [elt.uuid]: elt,
              })
            }
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) =>
  Object.assign({}, state.auth, state.subscriptions);

const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, {}, any>) => ({
  updatePrefs: (token: string, updates: ComponentPreference[]) =>
    dispatch(updateSubscriptionPreferences(token, updates)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyContentEditor);
